import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';
import { BLACKBODY_CHUNK } from './ember.js';

// Shared-rig contract: forbidden. The wall IS the light - a deck wash would
// raise the black the lamps are read against, and the afterglow of a cooling
// filament is the whole point.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'strobewall',
  family: 'stage',
  danceability: 0.80,
  energyLevel: 0.94,
  bpmMin: 120,
  bpmMax: 185,
  movement: 0.70,
  tonality: 0.40,
  prefers: { gridStrength: [0.65, 1.0], lowEnd: [0.50, 1.0], dynamics: [0.0, 0.60] },
  enabled: true
};

// StrobeWall: a wall of blinders aimed at the viewer, with the thermal inertia
// of real filament lamps.
//
// WHAT MAKES IT A BLINDER RATHER THAN A WHITE RECTANGLE. A filament does not
// switch. It heats in about 45 ms and cools in about 180 ms, so a burst ENDS as
// a slow orange fade rather than a cut, and every lamp on the wall carries a
// different point on that curve at any instant. Colour is read from the
// temperature through Ember's blackbody curve and never from an opacity: a lamp
// at 0.2 is a dark red filament in a black dish, a lamp at 0.95 is a white
// dish. Nothing anywhere in this file fades a white quad in and out.

// --- thermal ----------------------------------------------------------------
const HEAT_RATE = 22.0;          // ~45 ms to 63%
const COOL_RATE = 5.5;           // ~180 ms
const TEMP_OFF = 0.15;           // below this the glass is dark

// --- the wall ---------------------------------------------------------------
const PANEL_ROWS = 2;
// TWO LAMPS WIDE BY FOUR TALL, not four by two. The brief says "une grille de
// 4 x 2" without saying which way round, and the orientation decides whether
// the wall can be framed at all: five columns by two rows over a 16:9 frame is
// a PORTRAIT cell, and only a portrait panel tiles it. Read the other way the
// panel is landscape, the wall comes out 5:1, and no camera distance puts ten
// of them in the frame - which is exactly how the first build ended up as two
// thin banks with a floor between them. This way the wall is 10 lamps across by
// 8 down and it fills the picture.
const LAMP_COLS = 2;             // per panel
const LAMP_ROWS = 4;             // 2 x 4 = 8 lamps a panel
const WALL_Z = 0;
const WALL_OVERRUN = 1.20;       // the wall runs 20% off the frame on every side
// A lamp cell is stretched so the WALL's aspect matches the frame's and the
// overrun is the same on all four sides; clamped, because past these the cells
// stop reading as reflector boxes and the extra just becomes more overrun.
const CELL_ASPECT_MIN = 0.85;
const CELL_ASPECT_MAX = 1.90;
const LAMP_UNIT = 1.0;           // a lamp cell is one world unit tall
// The gap BETWEEN panels has to beat the gap between lamps of one panel, or
// eighty boxes read as one undifferentiated grid. At 0.045 of a panel cell
// against 0.10 of a lamp cell the two came out equal, which is why they did.
const PANEL_GAP = 0.135;         // share of a panel cell left to the frame
const LAMP_GAP = 0.055;          // and between lamps inside a panel
const FRAME_W = 0.085;           // bezel width, as a share of the panel
const PANEL_DEPTH_VAR = 0.03;    // +-3% of the camera distance
const LAMP_SLOP = 0.05;          // how badly a mis-seated lamp sits in its cell
const LAMP_SLOP_SHARE = 0.18;    // and how many of them are mis-seated
const DEAD_LAMPS = 3;            // blown, blackened, never lit
// The reflector is an OPEN BOX, not a quad: a front opening, a dish at the
// back, and four inner walls between them that the lamp lights. A flat quad in
// a wall of tilted panels reads as a broken parallelogram, which is what the
// first build drew.
const BOX_DEPTH = 0.42;          // of the cell's smaller side
const BOX_INNER = 0.58;          // dish size as a fraction of the opening
// AN UNPOWERED REFLECTOR IN THE DARK IS DARK. These are LINEAR values, SWEPT
// against the rendered frame rather than converted on paper: the pipeline is
// ACES plus bloom plus sRGB, and at the bottom of the range it is nothing like
// a gamma curve. Taking the sRGB inverse of the wanted 0.06 gives 0.0027, which
// measured at 0.0004 on screen - essentially black. 0.0220 is what actually
// lands on it. What was there before was a linear 0.068, measured at 0.27: the
// mid grey that left a lit box and an unlit one looking like relatives, and all
// of a blinder's aggression is in that gap.
const AMB_LIP = 0.0220;
const AMB_DEEP = 0.0072;
const AMB_DISH = 0.0048;
const FRAME_AMB = 0.0165;        // the bezel faces the room, so a shade above
// Tilt. The wall is NOT flat: columns turn inwards towards the viewer and rows
// tip up or down, with a per-panel wobble on top, so no two panels catch the
// eye at the same angle.
// THE WALL IS PLANE. Nothing here may bend it: no arc in z, no systematic yaw,
// no per-column toe-in. The first build had both - the outer columns were
// pushed back by a tenth of a cell and turned inwards by 9.2 degrees - and the
// two together made the left and right of the picture fall away into
// perspective, which is what put the housings edge-on and broke their shapes.
// What the spec asks for is only that the wall not be PERFECTLY flat: a few
// degrees of individual nod, and nothing else.
const TILT_MAX = 2 * Math.PI / 180;   // +-2 degrees, vertical nod only

const CAM_LOW = 0.05;            // slight low angle, in half-frame heights

// --- bursts -----------------------------------------------------------------
const PATTERNS = ['sequentiel', 'alterne', 'aleatoire', 'colonnes', 'diagonales'];
const BEAT_MS = [0.070, 0.120];  // short - the brevity is what makes a blinder
const BAR_MS = 0.060;            // the whole wall
const WAVE_BEATS = 2.0;          // a phrase sweep crosses in two beats
const CHASE_STEP = 0.050;
const CHASE_PASSES = 2;
const LAMP_STAGGER = 0.008;      // a few ms of spread inside a panel

// --- comfort. NOT aesthetic settings ----------------------------------------
const FULL_PER_SEC = 3;          // never more than three full-wall hits a second
const FULL_MIN_MS = 0.050;       // and never a shorter one than this
const LUM_LIMIT = 0.45;          // rolling one-second mean of the frame
const LUM_WINDOW = 1.0;
// COMFORT (b) IS GOVERNED FROM THE WALL'S OWN TEMPERATURE, not from a model of
// the finished frame. Three attempts were made at the latter - proportional,
// saturating, and two-variable to follow the gain - and all three failed in
// different parts of the range: the best left 0.064 of error near the limit,
// and during a drop chase the estimate read 0.148 where the true rolling mean
// was 0.235. An analytic estimate of a post-bloom, post-tone-mapped frame is
// not accurate to the few percent this constraint needs.
//
// The mean temperature of the wall is EXACT - state this file already owns,
// with no noise and no modelling in it - and measured against real frames over
// 1036 samples across every regime it correlates with the displayed one-second
// mean at 0.927, mean error 0.022. The fitted transfer below turns it into a
// predicted luminance, which is what ?strobelum reports, and a one-second mean
// wall temperature of 0.418 is what lands on the 0.45 limit.
const LUM_F = 0.098;             // frame luminance with the wall cold
const LUM_A = 1.59;
const LUM_K = 0.60;
// WHERE THE LIMIT ACTUALLY FALLS, read off the worst window of a two-minute run
// rather than off the fit, and re-read once the wall was reframed to fill the
// picture: the same wall temperature puts far more light on screen when ten
// panels cover the frame than when two banks sat at its edges, and the old
// threshold let the measured mean back up to 0.499. Both figures below are
// tuning results, swept against the true frame until the two-minute worst case
// came under 0.45 with the least attenuation that achieves it.
const WALL_LIMIT = 0.170;
// A SECOND, FASTER WINDOW. A one-second mean cannot be held by reacting to the
// one-second mean: by the time it reads high the light has already been on the
// screen for most of a second, and measured over two minutes of real playing
// the true rolling mean still reached 0.502 while the governor was only pulling
// the gain to 0.81. The short window sees a burst arrive and takes the gain
// down while there is still time for it to matter; its own threshold is higher,
// so a single flash still gets through at full strength - which is the point of
// a blinder.
const WALL_WINDOW_FAST = 0.28;
const WALL_LIMIT_FAST = 0.34;
// The gain dims the lamps sub-linearly once bloom and tone mapping are through
// with it - measured, a gain of 0.456 took a frame from 0.66 to 0.479 - so the
// correction is raised to a power rather than applied straight.
const GAIN_POWER = 1.6;
const PEAK = 0.96;               // a blinder has to bloom hard

// --- look -------------------------------------------------------------------
const FOG_DENSITY = 0.03;
const GEL_PANELS = 2;            // two of ten wear coloured gel
const HALO_MIN = 1.25;           // quad width in LAMP widths - a halo that is
const HALO_GROW = 1.5;           // narrower than its own bulb is not a halo
// Capped, and the cap is a performance number: the halo layer measured 10.7 ms
// on the Pi at 3.2, and the cost of an additive quad goes with the SQUARE of
// this. 2.4 gives back four fifths of that.
const HALO_MAX = 2.4;
const HALO_DARK = 0.045;         // below this the quad collapses to nothing
const CONE_LEN = 7.0;            // in lamp widths
const CONE_SPREAD = 1.45;
const CONE_FAN = 0.62;           // how far the shafts splay from the wall's axis

const TIER = {
  0: { cols: 3, filament: false, flare: false, cones: false, haze: false },
  1: { cols: 5, filament: true, flare: true, cones: false, haze: true },
  2: { cols: 5, filament: true, flare: true, cones: true, haze: true }
};
const DEFAULT_TIER = 2;

const _v3 = new THREE.Vector3();
const _m4 = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _scale = new THREE.Vector3();
const _col = new THREE.Color();

function clamp01(x) { return x < 0 ? 0 : (x > 1 ? 1 : x); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
// THE REFLECTOR BOX, a truncated pyramid: a small rectangle at the back, a
// larger opening at the front, four side faces joining them. Ten triangles -
// two for the dish and two for each side; the front stays open, which is what
// makes it a reflector rather than a closed box.
//
// Built from TWO RINGS of four corners, back and front, so the corner
// coordinates are shared by construction and no side can miss its neighbour's
// edge. The vertices are then emitted per face: the four sides need their own
// flat normals, and averaging them round the corners would round the box off.
//
// Winding: every side is wound so its INNER surface is the front face, since
// that is the only surface ever seen - we look into the reflector. Checked
// face by face rather than assumed, because a side wound the wrong way simply
// vanishes under FrontSide.
//
// aFace  0 on the dish, 1 on the sides
// aAlong 0 at the back, 1 at the lip: the gradient the lamp lights them with
function makeHousingGeometry(inner, depth) {
  const f = 0.5;
  const b = inner * 0.5;
  // The two rings, counter-clockwise seen from the room.
  const back = [[-b, -b], [b, -b], [b, b], [-b, b]];
  const front = [[-f, -f], [f, -f], [f, f], [-f, f]];

  const pos = [], nrm = [], uvs = [], face = [], along = [], idx = [];

  // The dish, facing the room.
  for (let i = 0; i < 4; i++) {
    pos.push(back[i][0], back[i][1], -depth);
    nrm.push(0, 0, 1);
    face.push(0);
    along.push(0);
  }
  uvs.push(0, 0, 1, 0, 1, 1, 0, 1);
  idx.push(0, 1, 2, 0, 2, 3);

  // Four sides. Side i joins ring corners i and i+1.
  for (let i = 0; i < 4; i++) {
    const j = (i + 1) % 4;
    const base = pos.length / 3;
    const b0 = back[i], b1 = back[j], f0 = front[i], f1 = front[j];
    pos.push(b0[0], b0[1], -depth, b1[0], b1[1], -depth,
             f1[0], f1[1], 0, f0[0], f0[1], 0);
    // Inward normal: perpendicular to the side, pointing at the axis.
    const ex = b1[0] - b0[0], ey = b1[1] - b0[1];          // along the back edge
    const sx = f0[0] - b0[0], sy = f0[1] - b0[1], sz = depth;   // up the slope
    // n = edge x slope, which for this winding comes out inward.
    let nx = ey * sz - 0 * sy;
    let ny = 0 * sx - ex * sz;
    let nz = ex * sy - ey * sx;
    const L = Math.hypot(nx, ny, nz) || 1;
    nx /= L; ny /= L; nz /= L;
    // The inward normal must point back towards the axis: check it against the
    // side's own midpoint rather than trusting the cross product's sign.
    const mx = (b0[0] + b1[0] + f0[0] + f1[0]) / 4;
    const my = (b0[1] + b1[1] + f0[1] + f1[1]) / 4;
    const flip = (nx * -mx + ny * -my) < 0 ? -1 : 1;
    for (let k = 0; k < 4; k++) nrm.push(nx * flip, ny * flip, nz * flip);
    uvs.push(0, 0, 1, 0, 1, 1, 0, 1);
    face.push(1, 1, 1, 1);
    along.push(0, 0, 1, 1);
    if (flip > 0) idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
    else idx.push(base, base + 2, base + 1, base, base + 3, base + 2);
  }

  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  g.setAttribute('aFace', new THREE.Float32BufferAttribute(face, 1));
  g.setAttribute('aAlong', new THREE.Float32BufferAttribute(along, 1));
  g.setIndex(idx);
  return g;
}

// The panel bezel: a flat rectangular annulus that sits a little proud of the
// lamp lips, so a panel reads as a frame full of lamps rather than as eight
// loose boxes. Eight triangles.
function makeFrameGeometry(w) {
  const o = 0.5, i = 0.5 - w;
  const outer = [[-o, -o], [o, -o], [o, o], [-o, o]];
  const inner = [[-i, -i], [i, -i], [i, i], [-i, i]];
  const pos = [], uvs = [], rim = [], idx = [];
  for (let k = 0; k < 4; k++) { pos.push(outer[k][0], outer[k][1], 0); rim.push(1); }
  for (let k = 0; k < 4; k++) { pos.push(inner[k][0], inner[k][1], 0); rim.push(0); }
  for (let k = 0; k < 8; k++) uvs.push(pos[k * 3], pos[k * 3 + 1]);
  for (let k = 0; k < 4; k++) {
    const j = (k + 1) % 4;
    idx.push(k, j, 4 + j, k, 4 + j, 4 + k);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  g.setAttribute('aRim', new THREE.Float32BufferAttribute(rim, 1));
  g.setIndex(idx);
  return g;
}

function hash1(n) {
  const s = Math.sin(n * 127.1) * 43758.5453;
  return s - Math.floor(s);
}

// =============================================================================
// SHADERS
// =============================================================================

const LAMP_VERTEX = `
  precision mediump float;
  attribute float aTemp;
  attribute float aNbr;
  attribute vec3 aGel;
  attribute float aDead;
  attribute float aFace;
  attribute float aAlong;
  varying vec2 vP;
  varying float vTemp;
  varying float vNbr;
  varying vec3 vGel;
  varying float vSafe;
  varying float vFace;
  varying float vAlong;
  varying float vDead;
${SAFE_ZONE_VERTEX_CHUNK}
  void main() {
    vP = uv * 2.0 - 1.0;
    vTemp = aTemp;
    vNbr = aNbr;
    vGel = aGel;
    vFace = aFace;
    vAlong = aAlong;
    vDead = aDead;
    vec4 clip = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0);
    vSafe = safeZoneAttenuation(clip);
    gl_Position = clip;
  }
`;

const LAMP_FRAGMENT = `
  precision mediump float;
  uniform float uGain;
  uniform float uFilament;
  uniform float uTempDebug;
  uniform float uSafeFloor;
  varying vec2 vP;
  varying float vTemp;
  varying float vNbr;
  varying vec3 vGel;
  varying float vSafe;
  varying float vFace;
  varying float vAlong;
  varying float vDead;
${BLACKBODY_CHUNK}

  void main() {
    if (uTempDebug > 0.5) { gl_FragColor = vec4(vec3(vTemp), 1.0); return; }

    float t = vTemp;
    vec3 metalBase = vec3(0.052, 0.055, 0.064);
    vec3 bbSelf0 = blackbody(t) * vGel;

    // THE INNER WALLS OF THE REFLECTOR. The geometry was already a well - the
    // wireframe check settles that - but with a FLAT ambient along the wall an
    // unlit box had no depth cue at all, and the only gradient on it was bright
    // at the centre and dark at the rim, which is the signature of a bump. Two
    // gradients now run in opposite directions and they are what make it read
    // as a hole:
    //
    //   AMBIENT rises towards the LIP. Room light reaches the mouth of a well
    //   and not its bottom, so an unlit box is a dark rectangular hole that
    //   fades inwards.
    //   THE LAMP falls off FROM the dish. It sits at the bottom, so the walls
    //   are brightest at their root and darken towards the opening, and the
    //   whole thing dims with the filament.
    //
    // No directional term: this is a light source inside a box, not a lamp on a
    // stage, so the four walls are lit alike and the wall you are looking at is
    // not brighter for being the lower one.
    if (vFace > 0.5) {
      float root = pow(1.0 - vAlong, 1.7);
      float ambient = mix(${AMB_DEEP.toFixed(5)}, ${AMB_LIP.toFixed(5)}, vAlong);
      // A thin specular catch on the lip and on the fold at the dish, cool
      // because what it reflects is the room and not the filament. It is all
      // that says "aluminium" when the lamp is out.
      float edgeLip = smoothstep(0.86, 1.0, vAlong);
      float edgeRoot = smoothstep(0.10, 0.0, vAlong);
      float spec = edgeLip * 0.030 + edgeRoot * 0.011;
      vec3 wcol = vec3(ambient) * vec3(0.90, 0.95, 1.12)
                + vec3(spec) * vec3(0.82, 0.90, 1.0)
                + metalBase * bbSelf0 * (13.0 * t * root)
                + bbSelf0 * (0.22 * t * root);
      wcol *= uGain;
      wcol = min(wcol, vec3(${PEAK.toFixed(2)}));
      wcol *= mix(uSafeFloor, 1.0, clamp((vSafe - 0.32) / 0.68, 0.0, 1.0));
      gl_FragColor = vec4(wcol, 1.0);
      return;
    }

    vec2 p = vP;
    float box = max(abs(p.x), abs(p.y));
    float r = length(p);

    // THE HOUSING. A dark metal dish with a raised rib around its mouth. It is
    // never black: it catches its own lamp and its neighbours', which is what
    // makes the wall an object instead of a grid of dots.
    // The dish is the deepest surface in the box, so it is also the darkest
    // when the lamp is out.
    float rib = smoothstep(0.76, 0.84, box) * smoothstep(1.02, 0.94, box);
    vec3 metal = vec3(${AMB_DISH.toFixed(5)}) * vec3(0.92, 0.96, 1.08);
    vec3 bbSelf = bbSelf0;
    // A DISH LIT BY ITS OWN FILAMENT REFLECTS RED, not grey. Left neutral, this
    // term is the one that keeps the cooling lamp from reading warm: measured
    // with a grey housing the red-to-blue ratio peaked at 4.9 instead of going
    // frank, because a rising neutral component sat under the blackbody the
    // whole way down.
    vec3 bbNbr = blackbody(vNbr) * vGel;
    vec3 col = metal * (1.0 + 1.9 * rib)                        // ambient
             + metalBase * (0.9 + 4.4 * rib) * 7.5 * (bbSelf * 0.55 * t + bbNbr * 0.45 * vNbr);

    // A BLOWN LAMP. Its envelope is smoked, so the dish goes darker still and
    // takes on the brown of burnt tungsten, densest over the bulb where the
    // deposit settles. It never lights - its temperature is pinned at zero
    // upstream - so this is the only thing that ever shows of it.
    if (vDead > 0.5) {
      float soot = smoothstep(0.52, 0.04, r);
      col = mix(col, col * vec3(0.30, 0.22, 0.19) * 0.45, soot);
      col *= 1.0 - 0.45 * soot;
    }

    // THE FILAMENT LIGHTS FIRST. Two strokes and a tie-bar across the middle of
    // the bulb, on a ramp that starts at 0.06 - while the disc's own ramp has
    // not begun. At the bottom of the curve all you see is a red wire in a dark
    // dish, which is exactly what a filament does.
    float fil = 0.0;
    if (uFilament > 0.5) {
      float inBulb = smoothstep(0.30, 0.24, r);
      float s1 = smoothstep(0.022, 0.006, abs(p.y - 0.075));
      float s2 = smoothstep(0.022, 0.006, abs(p.y + 0.075));
      float s3 = smoothstep(0.020, 0.006, abs(p.x)) * smoothstep(0.10, 0.06, abs(p.y));
      fil = (s1 + s2 + s3) * inBulb * smoothstep(0.045, 0.26, t);
    }

    // The bulb disc, then the dish itself going white at the top of the curve.
    float disc = smoothstep(0.32, 0.17, r) * smoothstep(0.24, 0.70, t);
    float dish = smoothstep(0.80, 0.12, r) * smoothstep(0.50, 1.00, t);

    vec3 bb = blackbody(t) * vGel;
    col += bb * (fil * 1.55 + disc * 1.15 + dish * 0.95);

    col *= uGain;
    col = min(col, vec3(${PEAK.toFixed(2)}));
    col *= mix(uSafeFloor, 1.0, clamp((vSafe - 0.32) / 0.68, 0.0, 1.0));
    gl_FragColor = vec4(col, 1.0);
  }
`;

// Halo and cross flare, one view-space billboard per lamp, sized by its own
// temperature in the vertex shader so the radius grows with the heat.
const GLOW_VERTEX = `
  precision mediump float;
  attribute float aTemp;
  attribute vec3 aGel;
  uniform float uHaloMin;
  uniform float uHaloGrow;
  uniform float uHaloMax;
  uniform float uLampSize;
  varying vec2 vP;
  varying float vTemp;
  varying vec3 vGel;
  varying float vSafe;
${SAFE_ZONE_VERTEX_CHUNK}
  void main() {
    vP = uv * 2.0 - 1.0;
    vTemp = aTemp;
    vGel = aGel;
    // A cold lamp has no halo, so its quad is collapsed to a point rather than
    // drawn and thrown away a fragment at a time. Most of the wall is cold most
    // of the time, and a degenerate triangle is free.
    float s = aTemp < ${HALO_DARK.toFixed(3)} ? 0.0
            : min(uHaloMax, uHaloMin + uHaloGrow * aTemp) * uLampSize;
    vec4 mv = modelViewMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
    mv.xy += position.xy * s;
    vec4 clip = projectionMatrix * mv;
    vSafe = safeZoneAttenuation(clip);
    gl_Position = clip;
  }
`;

const GLOW_FRAGMENT = `
  precision mediump float;
  uniform float uGain;
  uniform float uFlare;
  uniform float uSafeFloor;
  varying vec2 vP;
  varying float vTemp;
  varying vec3 vGel;
  varying float vSafe;
${BLACKBODY_CHUNK}
  void main() {
    float t = vTemp;
    float r = length(vP);
    // No discard: on a tiled GPU it defeats early-Z for the whole draw, and an
    // additive layer can simply write zero instead.
    float edge = max(0.0, 1.0 - r);

    float halo = edge * edge * smoothstep(0.10, 0.72, t) * 0.34;

    // THE CROSS FLARE, past 0.8 only. This is the glare, and without it the
    // wall reads as lit panels rather than as lamps pointed at you.
    float fl = 0.0;
    if (uFlare > 0.5) {
      float g = smoothstep(0.78, 0.96, t);
      // Shortened by a steeper falloff AND cut by a radial window, and its
      // strength taken down with it - because on screen the arm's REACH turns
      // out not to be the flare's to give. Measured: switching the flare off
      // altogether moves the arm at half-height by 2 px out of 178, and
      // quartering its gain moves it from 177 to 155. What carries that width
      // is the bloom around the whole lit lamp, and the halo is not mine to
      // touch. What the flare does own is the SATURATION - remove it and the
      // white pixels in a burst frame fall from 1.37% to 0.17% - so that is
      // what comes down here: 0.42 cuts them to 0.80%, a little over 40% less,
      // with the cross still plainly there. Capped at 0.92 as asked.
      float ax = exp(-abs(vP.y) * 26.0) * exp(-abs(vP.x) * 3.67);
      float ay = exp(-abs(vP.x) * 26.0) * exp(-abs(vP.y) * 3.67);
      float reach = smoothstep(0.60, 0.26, r);
      fl = min(g * (ax + ay) * edge * reach * 0.42, 0.92);
    }

    vec3 col = blackbody(t) * vGel * (halo + fl);
    col *= uGain;
    col = min(col, vec3(${PEAK.toFixed(2)}));
    col *= mix(uSafeFloor, 1.0, clamp((vSafe - 0.32) / 0.68, 0.0, 1.0));
    gl_FragColor = vec4(col, 1.0);
  }
`;

const FRAME_VERTEX = `
  precision mediump float;
  attribute float aTemp;
  attribute vec3 aGel;
  attribute float aRim;
  varying vec2 vXY;
  varying float vRim;
  varying float vTemp;
  varying vec3 vGel;
  varying float vSafe;
${SAFE_ZONE_VERTEX_CHUNK}
  void main() {
    vXY = position.xy;
    vRim = aRim;
    vTemp = aTemp;
    vGel = aGel;
    vec4 clip = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0);
    vSafe = safeZoneAttenuation(clip);
    gl_Position = clip;
  }
`;

const FRAME_FRAGMENT = `
  precision mediump float;
  uniform float uGain;
  uniform float uSafeFloor;
  uniform vec2 uScrew;             // screw centre, in panel units
  varying vec2 vXY;
  varying float vRim;
  varying float vTemp;
  varying vec3 vGel;
  varying float vSafe;
${BLACKBODY_CHUNK}
  void main() {
    float t = vTemp;
    // The bezel faces the room, so it sits a little above the reflector's own
    // ambient - but only a little.
    float bevel = smoothstep(0.0, 0.22, vRim) * smoothstep(1.0, 0.72, vRim);
    vec3 col = vec3(${FRAME_AMB.toFixed(5)}) * vec3(0.92, 0.95, 1.06) * (1.0 + 1.5 * bevel);

    // SCREWS at the four corners, with a slot. Four hash-free tests: the panel
    // is symmetrical, so one absolute-value comparison does all of them.
    vec2 d = abs(vXY) - uScrew;
    float r = length(d);
    float head = smoothstep(0.020, 0.013, r);
    float slot = smoothstep(0.0045, 0.0015, abs(d.x)) * smoothstep(0.016, 0.012, r);
    col += vec3(0.028, 0.030, 0.034) * head;
    col -= vec3(0.016) * slot;

    // Lit by its own panel, like everything else on the wall.
    col += vec3(0.018, 0.019, 0.022) * blackbody(t) * vGel * (2.6 * t) * (0.5 + 1.6 * bevel);
    col *= uGain;
    col = min(col, vec3(${PEAK.toFixed(2)}));
    col *= mix(uSafeFloor, 1.0, clamp((vSafe - 0.32) / 0.68, 0.0, 1.0));
    gl_FragColor = vec4(max(col, 0.0), 1.0);
  }
`;

// The visible shafts. One per PANEL, not per lamp: what gives the volume is a
// cone you can read, and eighty tiny ones cost more fill for less depth. The
// cone follows its panel's normal, so the wall's varied tilts send the shafts
// past the camera at different angles - a shaft aimed dead at the lens is a
// disc, not a cone.
const CONE_VERTEX = `
  precision mediump float;
  attribute float aTemp;
  attribute vec3 aGel;
  varying float vTemp;
  varying float vAlong;
  varying float vRim;
  varying vec3 vGel;
  varying float vFog;
  void main() {
    vTemp = aTemp;
    vGel = aGel;
    vAlong = clamp(position.y, 0.0, 1.0);   // 0 at the lamp, 1 at the mouth
    vec4 wp = instanceMatrix * vec4(position, 1.0);
    vec4 mv = modelViewMatrix * wp;
    // A SHELL, not a solid. Seen end-on a cone's open base fills with its own
    // far wall and reads as a flat disc - which is exactly what the first build
    // drew. Grazing the surface means a long path through the haze and a bright
    // edge; facing it means almost none. That single term is the difference
    // between a shaft of light and a blob.
    vec3 nw = normalize(mat3(modelViewMatrix) * mat3(instanceMatrix) * normal);
    vRim = 1.0 - abs(dot(nw, normalize(-mv.xyz)));
    vFog = exp(-length(mv.xyz) * length(mv.xyz) * ${(FOG_DENSITY * FOG_DENSITY).toFixed(6)});
    gl_Position = projectionMatrix * mv;
  }
`;

const CONE_FRAGMENT = `
  precision mediump float;
  uniform float uGain;
  varying float vTemp;
  varying float vAlong;
  varying float vRim;
  varying vec3 vGel;
  varying float vFog;
${BLACKBODY_CHUNK}
  void main() {
    float t = vTemp;
    // Bright at the lamp and thinning outwards, but only gently: the apex is a
    // POINT, so a steep falloff leaves the whole shaft dark and the layer
    // measured a peak of 5 out of 255. A floor plus a soft ramp keeps the body
    // of the beam readable, which is what carries the volume.
    float a = (0.28 + 0.72 * pow(1.0 - vAlong, 1.15)) * smoothstep(0.18, 0.80, t) * 0.95;
    a *= pow(clamp(vRim, 0.0, 1.0), 1.25);
    vec3 col = blackbody(max(t, 0.35)) * vGel * a * vFog;
    col *= uGain;
    gl_FragColor = vec4(min(col, vec3(${PEAK.toFixed(2)})), 1.0);
  }
`;

// The black structure the panels are bolted to. Never pure black - it carries a
// rib grid that picks up whichever panels are lit, and that is what stops the
// wall reading as a hole with dots in it.
const BACK_VERTEX = `
  precision mediump float;
  uniform vec3 uPanelPos[10];      // xy centre, z temperature
  uniform float uPanelCount;
  varying vec2 vW;
  varying float vFog;
  varying float vLit;
  void main() {
    vW = position.xy;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vFog = exp(-length(mv.xyz) * length(mv.xyz) * ${(FOG_DENSITY * FOG_DENSITY).toFixed(6)});
    // PER VERTEX, not per pixel. Ten panels with a divide each, run over every
    // pixel of a full-screen plane, measured at 17.6 ms on the Pi - a third of
    // the whole frame, and by a wide margin the most expensive thing in the
    // file. The falloff is smooth, so a subdivided plane interpolates it with
    // no visible difference for a few hundred vertices instead of half a
    // million fragments.
    float lit = 0.0;
    for (int i = 0; i < 10; i++) {
      if (float(i) >= uPanelCount) break;
      vec2 d = position.xy - uPanelPos[i].xy;
      lit += uPanelPos[i].z / (1.0 + dot(d, d) * 0.45);
    }
    vLit = lit;
    gl_Position = projectionMatrix * mv;
  }
`;

const BACK_FRAGMENT = `
  precision mediump float;
  uniform vec3 uTint;
  uniform vec2 uRib;
  uniform float uGain;
  varying vec2 vW;
  varying float vFog;
  varying float vLit;
  void main() {
    // A shallow rib grid, running with the panel pitch.
    vec2 g = abs(fract(vW / uRib) - 0.5);
    float rib = smoothstep(0.50, 0.42, max(g.x, g.y));
    vec3 base = mix(vec3(0.014, 0.015, 0.019), uTint * 0.05, 0.45);
    vec3 col = base * (1.0 + 1.6 * rib)
             + (vec3(0.95, 0.62, 0.30) * (0.35 + 0.9 * rib)) * vLit * 0.055;
    col *= vFog * uGain;
    gl_FragColor = vec4(min(col, vec3(0.55)), 1.0);
  }
`;

// The haze itself, as one additive sheet in front of the wall whose brightness
// follows the total heat on the wall. scene.fog does nothing for ShaderMaterial,
// so the atmosphere has to be drawn, not declared.
const HAZE_FRAGMENT = `
  precision mediump float;
  uniform vec3 uTint;
  uniform float uAmount;
  uniform float uGain;
  varying vec2 vUvH;
  void main() {
    vec2 p = vUvH * 2.0 - 1.0;
    float v = (1.0 - 0.45 * dot(p, p)) * uAmount * uGain;
    gl_FragColor = vec4(max(uTint * v, 0.0), 1.0);
  }
`;

const HAZE_VERTEX = `
  precision mediump float;
  varying vec2 vUvH;
  void main() {
    vUvH = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

// =============================================================================

export class StrobeWallVisualizer extends Visualizer {
  constructor(options) {
    super();
    const opts = options || {};
    let tier = opts.qualityTier != null ? opts.qualityTier : DEFAULT_TIER;
    if (!TIER[tier]) tier = 0;
    this._tier = tier;
    this._layout = TIER[tier];

    this._time = 0;
    this._paused = false;
    this._lastBeatPhase = 0;
    this._lastBarPhase = 0;
    this._lastPhraseIndex = null;
    this._pattern = 0;
    this._patternStep = 0;
    this._lastDropAt = -1e9;
    this._lowBand = 0.5;
    this._highBand = 0.5;
    this._lastLow = 0;
    this._lastNoGridAt = -1e9;
    this._fullFires = [];          // timestamps, for the three-a-second rule
    this._lumSamples = [];         // {t, v} over the rolling window
    this._lumMean = 0;
    this._gain = 1;
    this._lastPalette = null;
    this._tint = new THREE.Color(0x2a3550);
    this._skippedBars = 0;

    this._forceFire = parseInt(urlParam('strobefire'), 10);
    this._forceAll = urlParam('strobeall') === '1';
    this._tempDebug = urlParam('strobetemp') === '1';
    this._lumDebug = urlParam('strobelum') === '1';
    this._wireDebug = urlParam('strobewire') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const L = this._layout;

    this._cols = L.cols;
    this._panels = this._cols * PANEL_ROWS;
    this._perPanel = LAMP_COLS * LAMP_ROWS;
    this._lampCount = this._panels * this._perPanel;

    this._prevFog = scene.fog;
    scene.fog = new THREE.FogExp2(0x000000, FOG_DENSITY);

    // --- per-panel and per-lamp state ---------------------------------------
    this._panelFrom = new Float32Array(this._panels).fill(1e9);
    this._panelUntil = new Float32Array(this._panels);
    this._panelTemp = new Float32Array(this._panels);
    this._panelActive = this._panels;          // progress opens the wall up
    this._gel = [];
    for (let i = 0; i < this._panels; i++) this._gel.push(new THREE.Color(1, 1, 1));
    this._gelIndex = [];

    this._temp = new Float32Array(this._lampCount);
    this._target = new Float32Array(this._lampCount);
    this._lampPanel = new Int16Array(this._lampCount);
    this._lampDelay = new Float32Array(this._lampCount);
    this._lampAlive = new Uint8Array(this._lampCount).fill(1);

    // --- geometry ------------------------------------------------------------
    const lampGeo = makeHousingGeometry(BOX_INNER, BOX_DEPTH);
    this._tempAttr = new THREE.InstancedBufferAttribute(new Float32Array(this._lampCount), 1);
    this._nbrAttr = new THREE.InstancedBufferAttribute(new Float32Array(this._lampCount), 1);
    this._gelAttr = new THREE.InstancedBufferAttribute(new Float32Array(this._lampCount * 3), 3);
    for (let i = 0; i < this._lampCount * 3; i++) this._gelAttr.array[i] = 1;
    this._tempAttr.setUsage(THREE.DynamicDrawUsage);
    this._nbrAttr.setUsage(THREE.DynamicDrawUsage);

    this._deadAttr = new THREE.InstancedBufferAttribute(new Float32Array(this._lampCount), 1);

    this._lampMat = new THREE.ShaderMaterial({
      uniforms: Object.assign({
        uGain: { value: 1 },
        uFilament: { value: L.filament ? 1 : 0 },
        uTempDebug: { value: this._tempDebug ? 1 : 0 },
        uSafeFloor: { value: 0.55 }
      }, safeZoneUniforms),
      vertexShader: LAMP_VERTEX,
      fragmentShader: LAMP_FRAGMENT,
      side: THREE.FrontSide
    });
    this._lamps = new THREE.InstancedMesh(lampGeo, this._lampMat, this._lampCount);
    this._lamps.instanceMatrix.setUsage(THREE.StaticDrawUsage);
    this._lamps.frustumCulled = false;
    this._lamps.geometry.setAttribute('aTemp', this._tempAttr);
    this._lamps.geometry.setAttribute('aNbr', this._nbrAttr);
    this._lamps.geometry.setAttribute('aGel', this._gelAttr);
    this._lamps.geometry.setAttribute('aDead', this._deadAttr);
    scene.add(this._lamps);

    // The panel bezels. One draw call for ten frames, and they are what turn a
    // grid of eighty boxes into ten panels of eight.
    this._frameMat = new THREE.ShaderMaterial({
      uniforms: Object.assign({
        uGain: { value: 1 },
        uSafeFloor: { value: 0.55 },
        uScrew: { value: new THREE.Vector2(0.5 - FRAME_W * 0.5, 0.5 - FRAME_W * 0.5) }
      }, safeZoneUniforms),
      vertexShader: FRAME_VERTEX,
      fragmentShader: FRAME_FRAGMENT,
      side: THREE.DoubleSide
    });
    this._frames = new THREE.InstancedMesh(
      makeFrameGeometry(FRAME_W), this._frameMat, this._panels);
    this._frames.frustumCulled = false;
    this._frameTemp = new THREE.InstancedBufferAttribute(new Float32Array(this._panels), 1);
    this._frameGel = new THREE.InstancedBufferAttribute(new Float32Array(this._panels * 3), 3);
    for (let i = 0; i < this._panels * 3; i++) this._frameGel.array[i] = 1;
    this._frameTemp.setUsage(THREE.DynamicDrawUsage);
    this._frames.geometry.setAttribute('aTemp', this._frameTemp);
    this._frames.geometry.setAttribute('aGel', this._frameGel);
    scene.add(this._frames);

    this._glowMat = new THREE.ShaderMaterial({
      uniforms: Object.assign({
        uGain: { value: 1 },
        uFlare: { value: L.flare ? 1 : 0 },
        uHaloMin: { value: HALO_MIN },
        uHaloGrow: { value: HALO_GROW },
        uHaloMax: { value: HALO_MAX },
        uLampSize: { value: 1 },
        uSafeFloor: { value: 0.55 }
      }, safeZoneUniforms),
      vertexShader: GLOW_VERTEX,
      fragmentShader: GLOW_FRAGMENT,
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
      depthTest: false
    });
    this._glow = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), this._glowMat, this._lampCount);
    this._glow.frustumCulled = false;
    this._glow.renderOrder = 12;
    this._glowTemp = new THREE.InstancedBufferAttribute(new Float32Array(this._lampCount), 1);
    this._glowTemp.setUsage(THREE.DynamicDrawUsage);
    this._glow.geometry.setAttribute('aTemp', this._glowTemp);
    this._glow.geometry.setAttribute('aGel', this._gelAttr);
    scene.add(this._glow);

    if (L.cones) {
      // ConeGeometry puts its APEX at +h/2 and its mouth at -h/2, so the
      // obvious translate leaves the body buried in the wall - the first build
      // measured the whole cone layer at a luminance of exactly 0.0000. Mirror
      // first: apex at the lamp, mouth opening away into the room, which is
      // also the right way round for a beam.
      const coneGeo = new THREE.ConeGeometry(1, 1, 12, 1, true);
      coneGeo.scale(1, -1, 1);
      coneGeo.translate(0, 0.5, 0);
      this._coneMat = new THREE.ShaderMaterial({
        uniforms: { uGain: { value: 1 } },
        vertexShader: CONE_VERTEX,
        fragmentShader: CONE_FRAGMENT,
        blending: THREE.AdditiveBlending,
        transparent: true,
        depthWrite: false,
        depthTest: false,
        side: THREE.DoubleSide
      });
      this._cones = new THREE.InstancedMesh(coneGeo, this._coneMat, this._panels);
      this._cones.frustumCulled = false;
      this._cones.renderOrder = 8;
      this._coneTemp = new THREE.InstancedBufferAttribute(new Float32Array(this._panels), 1);
      this._coneGel = new THREE.InstancedBufferAttribute(new Float32Array(this._panels * 3), 3);
      for (let i = 0; i < this._panels * 3; i++) this._coneGel.array[i] = 1;
      this._coneTemp.setUsage(THREE.DynamicDrawUsage);
      this._cones.geometry.setAttribute('aTemp', this._coneTemp);
      this._cones.geometry.setAttribute('aGel', this._coneGel);
      scene.add(this._cones);
    }

    this._backUniforms = {
      uPanelPos: { value: [] },
      uPanelCount: { value: this._panels },
      uTint: { value: new THREE.Vector3(0.16, 0.20, 0.30) },
      uRib: { value: new THREE.Vector2(1, 1) },
      uGain: { value: 1 }
    };
    for (let i = 0; i < 10; i++) this._backUniforms.uPanelPos.value.push(new THREE.Vector3());
    this._backMat = new THREE.ShaderMaterial({
      uniforms: this._backUniforms,
      vertexShader: BACK_VERTEX,
      fragmentShader: BACK_FRAGMENT
    });
    this._back = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._backMat);
    this._back.renderOrder = -10;
    this._back.frustumCulled = false;
    scene.add(this._back);

    if (L.haze) {
      this._hazeMat = new THREE.ShaderMaterial({
        uniforms: {
          uTint: { value: new THREE.Vector3(0.22, 0.26, 0.40) },
          uAmount: { value: 0 },
          uGain: { value: 1 }
        },
        vertexShader: HAZE_VERTEX,
        fragmentShader: HAZE_FRAGMENT,
        blending: THREE.AdditiveBlending,
        transparent: true,
        depthWrite: false,
        depthTest: false
      });
      this._haze = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._hazeMat);
      this._haze.frustumCulled = false;
      this._haze.renderOrder = 20;
      scene.add(this._haze);
    }

    this._aspect = 0;
    this._buildWall();
    this._newPattern(0);
    if (this._wireDebug) this._buildWireDebug(scene, camera);
  }

  // ?strobewire=1 - ONE housing, in wireframe, from three quarters. The point
  // is to settle by eye whether the box OPENS towards the camera or POINTS at
  // it: seen face-on the two are the same silhouette, and which one you read is
  // carried entirely by the shading.
  _buildWireDebug(scene, camera) {
    for (const m of [this._lamps, this._glow, this._cones, this._back, this._haze,
                     this._frames]) {
      if (m) m.visible = false;
    }
    const g = makeHousingGeometry(BOX_INNER, BOX_DEPTH);
    this._wireSolid = new THREE.Mesh(g, new THREE.MeshBasicMaterial({
      color: 0x1d2a3a, side: THREE.FrontSide
    }));
    this._wireSolid.scale.set(1.4, 1.0, 1.0);
    scene.add(this._wireSolid);
    this._wireBox = new THREE.Mesh(g, new THREE.MeshBasicMaterial({
      color: 0x59ff9b, wireframe: true
    }));
    this._wireBox.scale.copy(this._wireSolid.scale);
    scene.add(this._wireBox);
    // The mouth is marked in red and the dish in yellow, so no reading is
    // ambiguous.
    const lip = new THREE.Mesh(new THREE.RingGeometry(0.66, 0.76, 4),
      new THREE.MeshBasicMaterial({ color: 0xff4d4d, side: THREE.DoubleSide }));
    lip.rotation.z = Math.PI / 4;
    lip.position.z = 0.001;
    this._wireBox.add(lip);
    const dish = new THREE.Mesh(new THREE.PlaneGeometry(0.16, 0.16),
      new THREE.MeshBasicMaterial({ color: 0xffd24a, side: THREE.DoubleSide }));
    dish.position.z = -BOX_DEPTH + 0.002;
    this._wireBox.add(dish);
    camera.position.set(1.7, 1.05, 2.0);
    camera.lookAt(0, 0, -BOX_DEPTH * 0.5);
    camera.updateMatrixWorld(true);
    this._wireReady = true;
  }

  // --- layout ----------------------------------------------------------------

  // The wall is sized from the FRUSTUM, not written down: it has to overrun the
  // frame at 16:9 and at the kiosk's 3.2:1, and a fixed extent cannot do both.
  _buildWall() {
    const cam = this._camera;
    const aspect = cam.aspect || 1.78;
    this._aspect = aspect;

    // THE WALL IS BUILT FIRST, THE CAMERA PLACED TO SUIT - never the other way
    // round, and never from a written-down distance. A lamp cell is one unit
    // tall and `cellAspect` wide; the wall is ten cells across by eight down.
    const lampsX = this._cols * LAMP_COLS;
    const lampsY = PANEL_ROWS * LAMP_ROWS;
    // Stretch the cell so the wall's own aspect matches the frame's: then the
    // overrun is the same 20% on all four sides. Clamped, and the clamp always
    // makes the wall BIGGER, never smaller - a wall that has stopped covering
    // the frame is the one failure this must not have.
    let cellAspect = aspect * lampsY / lampsX;
    let cellH = LAMP_UNIT;
    let cellW = cellAspect * cellH;
    if (cellAspect > CELL_ASPECT_MAX) { cellH = cellW / CELL_ASPECT_MAX; }
    else if (cellAspect < CELL_ASPECT_MIN) { cellW = cellH * CELL_ASPECT_MIN; }

    const wallW = cellW * lampsX;
    const wallH = cellH * lampsY;

    // Distance from the fov and the aspect, so the wall overruns by 20% on
    // whichever side binds.
    const tanHalf = Math.tan(cam.fov * Math.PI / 360);
    const dW = wallW / (2 * WALL_OVERRUN * tanHalf * aspect);
    const dH = wallH / (2 * WALL_OVERRUN * tanHalf);
    const dist = Math.min(dW, dH);
    const halfH = dist * tanHalf;
    const halfW = halfH * aspect;
    this._dist = dist;

    cam.position.set(0, -CAM_LOW * halfH, WALL_Z + dist);
    cam.lookAt(0, 0, WALL_Z);
    cam.updateMatrixWorld(true);

    const panCellW = wallW / this._cols;
    const panCellH = wallH / PANEL_ROWS;
    const panW = panCellW * (1 - PANEL_GAP);
    const panH = panCellH * (1 - PANEL_GAP);

    this._back.geometry.dispose();
    // Subdivided, because the panel lighting is interpolated across it.
    this._back.geometry = new THREE.PlaneGeometry(wallW * 1.6, wallH * 1.6, 32, 18);
    this._back.position.set(0, 0, WALL_Z - cellH * 0.55);
    this._backUniforms.uRib.value.set(cellW, cellH);

    const lampW = (panW / LAMP_COLS) * (1 - LAMP_GAP);
    const lampH = (panH / LAMP_ROWS) * (1 - LAMP_GAP);
    const lampSize = Math.min(lampW, lampH);
    this._glowMat.uniforms.uLampSize.value = lampSize;
    this._lampSize = lampSize;

    // NOTHING IS WITHHELD BEHIND THE COVER. The original brief asked for a
    // missing panel there and for no lamp behind the artwork; the wall is now
    // continuous and every lamp lights. That is a deliberate reversal of that
    // acceptance criterion, not an oversight. The safe zone still attenuates
    // the middle of the frame, so the artwork reads over a wall that goes on
    // working behind it.

    const posList = [];
    let alive = 0;
    for (let row = 0; row < PANEL_ROWS; row++) {
      for (let cx = 0; cx < this._cols; cx++) {
        const pi = row * this._cols + cx;
        const px = -wallW / 2 + panCellW * (cx + 0.5);
        const py = wallH / 2 - panCellH * (row + 0.5);
        const u = this._cols === 1 ? 0 : (cx / (this._cols - 1)) * 2 - 1;
        // No yaw and no depth: every panel sits in the same plane, square to
        // the camera. Only a small individual nod, within +-3 degrees.
        const yaw = 0;
        const pitch = (hash1(pi * 9.1) - 0.5) * 2 * TILT_MAX;
        // Each panel hangs at its own depth, within +-3% of the camera
        // distance. Eighty boxes on one exact plane is the thing that reads as
        // a texture rather than as hardware.
        const dz = (hash1(pi * 4.3) - 0.5) * 2 * PANEL_DEPTH_VAR * dist;
        posList.push({ x: px, y: py, z: WALL_Z + dz, yaw: yaw, pitch: pitch, u: u });
      }
    }
    this._panelPos = posList;

    for (let pi = 0; pi < this._panels; pi++) {
      const P = posList[pi];
      const v = this._backUniforms.uPanelPos.value[pi];
      if (v) v.set(P.x, P.y, 0);
      _e.set(P.pitch, P.yaw, 0, 'YXZ');
      _q.setFromEuler(_e);

      for (let ly = 0; ly < LAMP_ROWS; ly++) {
        for (let lx = 0; lx < LAMP_COLS; lx++) {
          const li = pi * this._perPanel + ly * LAMP_COLS + lx;
          this._lampPanel[li] = pi;
          this._lampDelay[li] = hash1(li * 5.3) * LAMP_STAGGER;
          // Lamp offset inside the panel, in the panel's own plane.
          let ox = (-panW / 2) + (panW / LAMP_COLS) * (lx + 0.5);
          let oy = (panH / 2) - (panH / LAMP_ROWS) * (ly + 0.5);
          // A few lamps sit badly in their housing. Not many - the point is
          // that the eye catches one, not that the wall looks broken.
          if (hash1(li * 2.9 + 11.0) < LAMP_SLOP_SHARE) {
            ox += (hash1(li * 7.7) - 0.5) * 2 * LAMP_SLOP * (panW / LAMP_COLS);
            oy += (hash1(li * 13.1) - 0.5) * 2 * LAMP_SLOP * (panH / LAMP_ROWS);
          }
          _v3.set(ox, oy, 0).applyQuaternion(_q);
          _v3.x += P.x; _v3.y += P.y; _v3.z += P.z;

          this._lampAlive[li] = 1;
          alive++;
          // BLOWN LAMPS. They stay in the wall, dark and smoked, and never
          // light again.
          this._deadAttr.array[li] = 0;

          _scale.set(lampW, lampH, Math.min(lampW, lampH));
          _m4.compose(_v3, _q, _scale);
          this._lamps.setMatrixAt(li, _m4);
          this._glow.setMatrixAt(li, _m4);

        }
      }

      if (this._frames) {
        _e.set(P.pitch, P.yaw, 0, 'YXZ');
        _q.setFromEuler(_e);
        _v3.set(P.x, P.y, P.z + BOX_DEPTH * lampSize * 0.10);
        _scale.set(panCellW, panCellH, 1);
        _m4.compose(_v3, _q, _scale);
        this._frames.setMatrixAt(pi, _m4);
      }

      if (this._cones) {
        // The SHAFT fans out from the middle of the wall; the PANEL does not
        // move. A rig aims its blinders at different parts of a room, so the
        // beams diverge even though the frames are all bolted to one flat
        // truss - and with the wall now square to the camera, taking the beam
        // direction from the panel's own normal would point every shaft
        // straight down the lens, where a cone is a disc.
        const spreadX = P.u * CONE_FAN;
        const spreadY = (P.y > 0 ? 1 : -1) * CONE_FAN * 0.55;
        const toCam = new THREE.Quaternion().setFromUnitVectors(
          new THREE.Vector3(0, 1, 0),
          new THREE.Vector3(spreadX, spreadY, 1).normalize());
        _v3.set(P.x, P.y, P.z + 0.05);
        _scale.set(CONE_SPREAD * lampSize, CONE_LEN * lampSize, CONE_SPREAD * lampSize);
        _m4.compose(_v3, toCam, _scale);
        this._cones.setMatrixAt(pi, _m4);
      }
    }
    // Pick the blown ones deterministically, spread across different panels so
    // they do not cluster.
    this._dead = [];
    for (let k = 0; k < DEAD_LAMPS; k++) {
      const panel = Math.floor(hash1(k * 31.7 + 3.3) * this._panels);
      const slot = Math.floor(hash1(k * 17.9 + 8.1) * this._perPanel);
      const li = panel * this._perPanel + slot;
      if (li < this._lampCount && this._lampAlive[li] && this._dead.indexOf(li) < 0) {
        this._dead.push(li);
        this._deadAttr.array[li] = 1;
      }
    }
    this._deadAttr.needsUpdate = true;
    this._aliveCount = alive;
    this._lamps.instanceMatrix.needsUpdate = true;
    this._glow.instanceMatrix.needsUpdate = true;
    if (this._frames) this._frames.instanceMatrix.needsUpdate = true;
    if (this._cones) this._cones.instanceMatrix.needsUpdate = true;

    this._buildNeighbours();
    this._applyGels();
  }

  // Mean of the four orthogonal neighbours, so a housing catches the lamps
  // beside it and not only its own.
  _buildNeighbours() {
    this._nbrIdx = [];
    for (let li = 0; li < this._lampCount; li++) {
      const pi = this._lampPanel[li];
      const k = li - pi * this._perPanel;
      const lx = k % LAMP_COLS;
      const ly = (k / LAMP_COLS) | 0;
      const list = [];
      if (lx > 0) list.push(li - 1);
      if (lx < LAMP_COLS - 1) list.push(li + 1);
      if (ly > 0) list.push(li - LAMP_COLS);
      if (ly < LAMP_ROWS - 1) list.push(li + LAMP_COLS);
      this._nbrIdx.push(list);
    }
  }

  _applyGels() {
    this._gelIndex = [];
    // Two panels in ten wear gel, spread apart so the colour is not a clump.
    const step = Math.max(1, Math.floor(this._panels / GEL_PANELS));
    for (let g = 0; g < GEL_PANELS && g * step < this._panels; g++) {
      this._gelIndex.push((g * step + 1) % this._panels);
    }
    for (let pi = 0; pi < this._panels; pi++) this._gel[pi].setRGB(1, 1, 1);
    const vivid = (this._lastPalette && this._lastPalette.vividColors
      && this._lastPalette.vividColors.length) ? this._lastPalette.vividColors : null;
    for (let g = 0; g < this._gelIndex.length; g++) {
      const pi = this._gelIndex[g];
      if (vivid) {
        _col.set(vivid[g % vivid.length]);
        // A gel FILTERS, it never adds: keep it light enough that the thermal
        // ramp still reads through it.
        this._gel[pi].setRGB(0.35 + 0.65 * _col.r, 0.35 + 0.65 * _col.g, 0.35 + 0.65 * _col.b);
      } else {
        this._gel[pi].setRGB(g === 0 ? 0.55 : 1.0, g === 0 ? 0.70 : 0.62, g === 0 ? 1.0 : 0.45);
      }
    }
    for (let li = 0; li < this._lampCount; li++) {
      const c = this._gel[this._lampPanel[li]];
      this._gelAttr.array[li * 3] = c.r;
      this._gelAttr.array[li * 3 + 1] = c.g;
      this._gelAttr.array[li * 3 + 2] = c.b;
    }
    this._gelAttr.needsUpdate = true;
    if (this._frameGel) {
      for (let pi = 0; pi < this._panels; pi++) {
        const c = this._gel[pi];
        this._frameGel.array[pi * 3] = c.r;
        this._frameGel.array[pi * 3 + 1] = c.g;
        this._frameGel.array[pi * 3 + 2] = c.b;
      }
      this._frameGel.needsUpdate = true;
    }
    if (this._coneGel) {
      for (let pi = 0; pi < this._panels; pi++) {
        const c = this._gel[pi];
        this._coneGel.array[pi * 3] = c.r;
        this._coneGel.array[pi * 3 + 1] = c.g;
        this._coneGel.array[pi * 3 + 2] = c.b;
      }
      this._coneGel.needsUpdate = true;
    }
  }

  // --- bursts ----------------------------------------------------------------

  _newPattern(seed) {
    this._pattern = (Math.abs(seed | 0) + ((Math.random() * PATTERNS.length) | 0)) % PATTERNS.length;
    this._patternStep = 0;
  }

  _panelsForBeat(count) {
    const out = [];
    const n = this._panelActive;
    const kind = PATTERNS[this._pattern];
    const cols = this._cols;
    for (let k = 0; k < count; k++) {
      const s = this._patternStep + k;
      let pi;
      if (kind === 'sequentiel') {
        pi = s % n;
      } else if (kind === 'alterne') {
        pi = ((s % 2) * (n >> 1) + ((s / 2) | 0)) % n;
      } else if (kind === 'aleatoire') {
        pi = (Math.random() * n) | 0;
      } else if (kind === 'colonnes') {
        const c = s % cols;
        pi = ((s / cols) | 0) % PANEL_ROWS * cols + c;
      } else {
        const c = s % cols;
        const r = (s + ((s / cols) | 0)) % PANEL_ROWS;
        pi = r * cols + c;
      }
      pi = pi % this._panels;
      if (out.indexOf(pi) < 0) out.push(pi);
    }
    this._patternStep += count;
    return out;
  }

  _fire(pi, from, dur) {
    // EVERY panel fires. The exclusion that used to sit here dated from the
    // build that blanked the panel behind the cover wholesale, and it outlived
    // its reason: once the cull went per-lamp, one panel in ten had quietly
    // stopped lighting at all while its geometry still counted as present.
    if (pi < 0 || pi >= this._panels) return;
    // Extending an already-lit panel rather than restarting it: a filament that
    // is still hot does not re-heat, it just stays on.
    if (from <= this._time && this._panelUntil[pi] > this._time) {
      this._panelUntil[pi] = Math.max(this._panelUntil[pi], from + dur);
      return;
    }
    this._panelFrom[pi] = from;
    this._panelUntil[pi] = from + dur;
  }

  // COMFORT (a) and (c). Three full-wall hits a second is the ceiling, and at
  // high bpm the answer is to SKIP a bar, not to shorten the flash - (c) says a
  // short flash flickers harder than a long one, so the duration floor holds
  // even when the rate limiter is biting.
  _fireWall(dur) {
    const d = Math.max(FULL_MIN_MS, dur);
    while (this._fullFires.length && this._time - this._fullFires[0] > 1.0) this._fullFires.shift();
    if (this._fullFires.length >= FULL_PER_SEC) { this._skippedBars++; return false; }
    this._fullFires.push(this._time);
    for (let pi = 0; pi < this._panels; pi++) this._fire(pi, this._time, d);
    return true;
  }

  _fireWave(beatDur) {
    const per = (WAVE_BEATS * beatDur) / Math.max(1, this._cols);
    for (let cx = 0; cx < this._cols; cx++) {
      for (let row = 0; row < PANEL_ROWS; row++) {
        this._fire(row * this._cols + cx, this._time + cx * per, per * 1.5);
      }
    }
  }

  // COMFORT (d): two passes, and the count is structural - the loop cannot run
  // a third even if a second drop lands inside the first.
  _fireChase() {
    for (let pass = 0; pass < CHASE_PASSES; pass++) {
      for (let pi = 0; pi < this._panels; pi++) {
        this._fire(pi, this._time + (pass * this._panels + pi) * CHASE_STEP, CHASE_STEP * 1.4);
      }
    }
  }

  // --- the frame -------------------------------------------------------------

  update(deltaTime, profile) {
    const dt = Math.min(deltaTime, 0.05);
    this._paused = profile.playbackState === 'PAUSED';
    // pause freezes the temperatures exactly where they are.
    if (this._paused) return;
    this._time += dt;

    if (this._wireDebug) return;
    if (this._camera && Math.abs((this._camera.aspect || 1.78) - this._aspect) > 1e-3) {
      this._buildWall();
    }

    const director = profile.director || {};
    const bands = director.bands || { low: 0.5, mid: 0.5, high: 0.5 };
    this._lowBand = clamp01(bands.low);
    this._highBand = clamp01(bands.high);

    const palette = profile.artworkPalette;
    if (palette && palette !== this._lastPalette) {
      this._lastPalette = palette;
      const dom = (palette.dominantColors && palette.dominantColors.length)
        ? palette.dominantColors[0] : 0x2a3550;
      _col.set(dom);
      this._tint.copy(_col);
      this._backUniforms.uTint.value.set(_col.r, _col.g, _col.b);
      if (this._hazeMat) this._hazeMat.uniforms.uTint.value.set(0.20 + _col.r * 0.55, 0.22 + _col.g * 0.62, 0.28 + _col.b * 0.80);
      this._applyGels();
    }

    // progress opens the wall from six panels to all of them at mid-track.
    const arc = Math.sin(clamp01(profile.progress || 0) * Math.PI);
    const openable = Math.max(1, this._panels - 4);
    this._panelActive = Math.min(this._panels, Math.round(openable + (this._panels - openable) * arc));

    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 140;
    const beatDur = Math.max(0.20, Math.min(1.2, 60 / bpm));

    const beatPhase = profile.beatPhase || 0;
    const barPhase = profile.barPhase || 0;
    const hasGrid = director.hasReliableGrid !== false && profile.beatPhase != null;

    let barHit = false;
    if (hasGrid) {
      if (barPhase < this._lastBarPhase) { this._fireWall(BAR_MS); barHit = true; }
      if (beatPhase < this._lastBeatPhase && !barHit) this._beatBurst();
      this._lastBarPhase = barPhase;
      this._lastBeatPhase = beatPhase;
      const phrase = profile.phraseIndex;
      if (phrase != null && this._lastPhraseIndex != null && phrase !== this._lastPhraseIndex) {
        this._newPattern(phrase);
        this._fireWave(beatDur);
      }
      this._lastPhraseIndex = phrase;
    } else {
      // NO BPM. Bursts ride the rises in the low band, and the wall stays dense:
      // a floor fires a burst anyway if nothing has happened for half a second.
      const rise = this._lowBand - this._lastLow;
      const since = this._time - this._lastNoGridAt;
      if ((rise > 0.10 && since > 0.16) || since > 0.50) {
        this._lastNoGridAt = this._time;
        if (rise > 0.22) this._fireWall(BAR_MS); else this._beatBurst();
      }
      this._lastLow = this._lowBand;
    }

    const ev = director.nextEvent;
    if (ev && ev.type === 'drop' && ev.msUntil != null && ev.msUntil <= 0
        && this._time - this._lastDropAt > 2.0) {
      this._lastDropAt = this._time;
      this._fireChase();
    }

    if (this._forceAll) this._fireWall(BAR_MS);
    if (Number.isFinite(this._forceFire)) this._fire(this._forceFire % this._panels, this._time, 0.12);

    this._step(dt);
  }

  _beatBurst() {
    // bands.low sets how many panels go at once, bands.high how short the hit
    // is: the brighter the top end the snappier the wall.
    const n = 1 + Math.round(2 * clamp01(this._lowBand));
    const dur = BEAT_MS[1] - (BEAT_MS[1] - BEAT_MS[0]) * clamp01(this._highBand);
    const list = this._panelsForBeat(n);
    for (let i = 0; i < list.length; i++) this._fire(list[i], this._time, dur);
  }

  // THE THERMAL STEP. One exponential approach per lamp with two different
  // rates, and the whole look comes out of the asymmetry between them.
  _step(dt) {
    const t = this._time;
    const heat = Math.min(1, HEAT_RATE * dt);
    const cool = Math.min(1, COOL_RATE * dt);   // clamped: a long frame must not
                                                // overshoot into a negative temp
    for (let pi = 0; pi < this._panels; pi++) this._panelTemp[pi] = 0;

    for (let li = 0; li < this._lampCount; li++) {
      if (!this._lampAlive[li]) { this._temp[li] = 0; continue; }
      const pi = this._lampPanel[li];
      const from = this._panelFrom[pi] + this._lampDelay[li];
      const on = (this._deadAttr.array[li] > 0.5
        || !(t >= from && t < this._panelUntil[pi])) ? 0 : 1;
      const cur = this._temp[li];
      const next = cur + (on - cur) * (on > cur ? heat : cool);
      this._temp[li] = next;
      this._tempAttr.array[li] = next;
      this._glowTemp.array[li] = next;
      this._panelTemp[pi] += next;
    }
    for (let pi = 0; pi < this._panels; pi++) this._panelTemp[pi] /= this._perPanel;

    for (let li = 0; li < this._lampCount; li++) {
      const list = this._nbrIdx[li];
      let s = 0;
      for (let k = 0; k < list.length; k++) s += this._temp[list[k]];
      this._nbrAttr.array[li] = list.length ? s / list.length : 0;
    }
    this._tempAttr.needsUpdate = true;
    this._nbrAttr.needsUpdate = true;
    this._glowTemp.needsUpdate = true;

    let wall = 0;
    for (let pi = 0; pi < this._panels; pi++) {
      const v = this._backUniforms.uPanelPos.value[pi];
      if (v) v.z = this._panelTemp[pi];
      if (this._coneTemp) this._coneTemp.array[pi] = this._panelTemp[pi];
      if (this._frameTemp) this._frameTemp.array[pi] = this._panelTemp[pi];
      wall += this._panelTemp[pi];
    }
    wall /= this._panels;
    if (this._coneTemp) this._coneTemp.needsUpdate = true;
    if (this._frameTemp) this._frameTemp.needsUpdate = true;
    // Swept: 0.16 contributed 0.0000 to the frame, 0.62 washed it to grey.
    if (this._hazeMat) this._hazeMat.uniforms.uAmount.value = wall * 0.095;

    this._governLuminance(wall, dt);
  }

  // COMFORT (b). The one-second mean is taken on the WALL, and turned into a
  // predicted frame luminance only for reporting - see the note on LUM_F.
  _governLuminance(wall, dt) {
    this._lumSamples.push([this._time, wall]);
    while (this._lumSamples.length && this._time - this._lumSamples[0][0] > LUM_WINDOW) {
      this._lumSamples.shift();
    }
    let sum = 0;
    let sumFast = 0;
    let nFast = 0;
    for (let i = 0; i < this._lumSamples.length; i++) {
      sum += this._lumSamples[i][1];
      if (this._time - this._lumSamples[i][0] <= WALL_WINDOW_FAST) {
        sumFast += this._lumSamples[i][1];
        nFast++;
      }
    }
    // One sample a frame at a near-constant dt, so the plain mean over the
    // window IS the time average.
    this._wallMean = sum / Math.max(1, this._lumSamples.length);
    this._wallFast = sumFast / Math.max(1, nFast);
    this._lumMean = LUM_F + LUM_A * (1 - Math.exp(-LUM_K * this._wallMean));

    // FEED-FORWARD, not feedback: the wall's temperature does not depend on the
    // gain, so there is no loop here to hunt. The earlier version fed a
    // gain-dependent estimate back into itself and duly oscillated.
    // Whichever window is further over wins.
    const overSlow = this._wallMean / WALL_LIMIT;
    const overFast = this._wallFast / WALL_LIMIT_FAST;
    const over = Math.max(overSlow, overFast);
    const want = over > 1 ? Math.max(0.25, Math.pow(1 / over, GAIN_POWER)) : 1;
    // Down fast, up slowly: an attenuation that released quickly would itself
    // flicker at the limit.
    const k = want < this._gain ? Math.min(1, 16 * dt) : Math.min(1, 2.2 * dt);
    if (!this._gainLocked) {
      this._gain += (want - this._gain) * k;
      this._gain = Math.max(0.25, Math.min(1, this._gain));
    }

    this._lampMat.uniforms.uGain.value = this._gain;
    this._glowMat.uniforms.uGain.value = this._gain;
    this._backUniforms.uGain.value = this._gain;
    if (this._coneMat) this._coneMat.uniforms.uGain.value = this._gain;
    if (this._frameMat) this._frameMat.uniforms.uGain.value = this._gain;
    if (this._hazeMat) this._hazeMat.uniforms.uGain.value = this._gain;

    if (this._lumDebug) this._showLum();
  }

  _showLum() {
    if (typeof document === 'undefined') return;
    if (!this._lumEl) {
      this._lumEl = document.createElement('div');
      this._lumEl.style.cssText = 'position:fixed;left:10px;top:10px;z-index:9999;'
        + 'font:12px monospace;color:#8f8;background:rgba(0,0,0,.6);padding:6px 8px;'
        + 'white-space:pre;pointer-events:none';
      document.body.appendChild(this._lumEl);
    }
    if ((this._lumFrames = (this._lumFrames || 0) + 1) % 6) return;
    this._lumEl.textContent = 'strobewall\n moyenne 1 s ' + this._lumMean.toFixed(3)
      + '  / limite ' + LUM_LIMIT.toFixed(2)
      + '\n mur 1 s   ' + (this._wallMean || 0).toFixed(3) + '  / seuil 0.338'
      + '\n gain      ' + this._gain.toFixed(3)
      + '\n plein mur ' + this._fullFires.length + '/s  (sautes ' + this._skippedBars + ')';
  }

  dispose() {
    const scene = this._scene;
    if (scene) {
      scene.fog = this._prevFog || null;
      if (this._lamps) scene.remove(this._lamps);
      if (this._glow) scene.remove(this._glow);
      if (this._cones) scene.remove(this._cones);
      if (this._frames) scene.remove(this._frames);
      if (this._back) scene.remove(this._back);
      if (this._haze) scene.remove(this._haze);
    }
    if (scene) {
      if (this._wireBox) scene.remove(this._wireBox);
      if (this._wireSolid) scene.remove(this._wireSolid);
    }
    const kill = (m) => { if (m) { m.geometry.dispose(); m.material.dispose(); } };
    kill(this._lamps); kill(this._glow); kill(this._cones);
    kill(this._back); kill(this._haze); kill(this._frames);
    this._frames = null;
    kill(this._wireBox); kill(this._wireSolid);
    this._wireBox = this._wireSolid = null;
    this._lamps = this._glow = this._cones = this._back = this._haze = null;
    if (this._lumEl && this._lumEl.parentNode) this._lumEl.parentNode.removeChild(this._lumEl);
    this._lumEl = null;
  }
}
